import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  GraduationCap, Briefcase, Users, Globe, Building,
  Home, FileCheck, Shield, BookOpen, ArrowRight, CheckCircle
} from "lucide-react";

const studentServices = [
  {
    icon: Home,
    title: "Housing Support",
    description: "Finding the right accommodation in the United States, from dormitories to host families.",
  },
  {
    icon: FileCheck,
    title: "Documentation Guidance",
    description: "Complete instructions on obtaining and maintaining F1 legal status as an international student.",
  },
  {
    icon: Shield,
    title: "Health Insurance",
    description: "Application services and benefits support to ensure you're covered throughout your studies.",
  },
  {
    icon: GraduationCap,
    title: "Admission Assistance",
    description: "Expert guidance in choosing and applying to universities that match your goals.",
  },
];

const orgServices = [
  {
    icon: Users,
    title: "Performance Enhancement",
    description: "Monitoring students' academic development with regular progress reports.",
  },
  {
    icon: Building,
    title: "Institutional Liaison",
    description: "Maintaining relationships with partnered institutions for optimal student integration.",
  },
  {
    icon: BookOpen,
    title: "Academic Reports",
    description: "Keeping sponsors informed about students' academic progress and achievements.",
  },
  {
    icon: Briefcase,
    title: "Executive Training",
    description: "Short courses and in-company training for executives across various fields.",
  },
];

const programs = [
  {
    title: "Higher Education Access",
    description: "Connect with top universities and academic institutions worldwide for undergraduate, graduate, and doctoral programs.",
    features: ["University placement", "Application support", "Visa assistance", "Pre-departure orientation"],
    image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600",
  },
  {
    title: "Language Programs",
    description: "Immerse yourself in American culture with language courses and host family accommodation.",
    features: ["ESL courses", "Host family stays", "Cultural immersion", "Academic English"],
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600",
  },
  {
    title: "Professional Development",
    description: "Skill-building programs and certifications for career advancement in the global marketplace.",
    features: ["Certification programs", "Career coaching", "Industry connections", "Alumni network"],
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600",
  },
];

const ServicesPage = () => {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-[#060B19] to-[#060B19]" />
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[200px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              Our Services
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Comprehensive Support for{" "}
              <span className="gradient-text">Global Success</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed">
              From students seeking international education to organizations managing 
              academic programs, we provide tailored solutions for every need.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Student Services */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              For Students
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Student Support Services
            </h2>
            <p className="text-white/60 max-w-2xl">
              Our comprehensive student services ensure your journey from home to 
              campus is smooth and successful.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {studentServices.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-6 card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Organization Services */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-secondary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              For Organizations
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Institutional Services
            </h2>
            <p className="text-white/60 max-w-2xl">
              We partner with institutions and sponsors to manage academic programs 
              and ensure student success.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {orgServices.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-6 card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              Our Programs
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Education Pathways
            </h2>
          </motion.div>

          <div className="space-y-12">
            {programs.map((program, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  idx % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={idx % 2 === 1 ? "lg:order-2" : ""}>
                  <h3 className="font-heading text-2xl font-bold text-white mb-4">{program.title}</h3>
                  <p className="text-white/60 leading-relaxed mb-6">{program.description}</p>
                  <ul className="space-y-3">
                    {program.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary" />
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={idx % 2 === 1 ? "lg:order-1" : ""}>
                  <img
                    src={program.image}
                    alt={program.title}
                    className="rounded-2xl w-full h-64 object-cover"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Student Requirements
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Here's what you'll need to begin your journey with M2L.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl p-8"
            >
              <h3 className="font-heading text-xl font-bold text-white mb-6 border-b border-white/10 pb-4">
                Essential Documents
              </h3>
              <ul className="space-y-4">
                {[
                  "Valid F1 visa",
                  "Valid I-20 from an accredited institution",
                  "Valid passport (six months or longer expiration)",
                  "Information on place of residence in the US",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl p-8"
            >
              <h3 className="font-heading text-xl font-bold text-white mb-6 border-b border-white/10 pb-4">
                Additional Requirements
              </h3>
              <ul className="space-y-4">
                {[
                  "Previous academic records and certificates",
                  "Proper sponsorship letter",
                  "Cash amount per country regulations",
                  "Updated immunization reports",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-[#060B19] to-primary/10 border-y border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Begin?
          </h2>
          <p className="text-white/60 mb-8">
            Contact us today to learn how we can support your educational journey.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-[#060B19] rounded-lg font-semibold hover:bg-primary-hover transition-all group"
          >
            Contact Us
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
